import { AuthService, authServiceFactory } from 'src/services/auth.service';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, inject, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { RouterModule, Routes } from '@angular/router';
import { AboutComponent } from './components/about/about.component';
import { AppComponent } from './app.component';
import { BannerComponent } from './components/banner/banner.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { BuildMode } from 'src/environments/environment.base';
import { CdkDragPlaceholder } from '@angular/cdk/drag-drop';
import { CreateFilterConfigDialogComponent } from './components/sidebar/create-filter-config-dialog/create-filter-config-dialog.component';
import { DemoTagComponent } from './components/demo-tag/demo-tag.component';
import { DropdownLazyComponent } from './components/utils/dropdown-lazy/dropdown-lazy.component';
import { EasterEggComponent } from './components/easter-egg/easter-egg.component';
import { FavoriteToggleComponent } from './components/favorite-toggle/favorite-toggle.component';
import { HomeComponent } from './components/home/home.component';
import { LoginComponent } from './components/login/login.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { RouteReuseService } from 'src/services/route-reuse.service';
import { RouteReuseStrategy } from '@angular/router';
import { SearchLazyComponent } from './components/utils/search-lazy/search-lazy.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { TreeComponent } from './components/tree/tree.component';
import { UnauthorizedInterceptor } from 'src/interceptors/http-interceptor';
import { WorkspaceDialogComponent } from './components/workspace-dialog/workspace-dialog.component';
import { authGuard } from 'src/guards/auth.guard';
import { configReducer } from '../store/reducers/config.reducer';
import { environment } from '../environments/environment';
import { guestGuard } from 'src/guards/guest.guard';
import { storageMetaReducer } from 'src/store/reducers/meta.reducer';
import { userInfoReducer } from 'src/store/reducers/user-info.reducer';
import { writeAccessGuard } from 'src/guards/write-access.guard';

// PrimeNg
// eslint-disable-next-line sort-imports
import { ConfirmationService, MessageService } from 'primeng/api';
import { AccordionModule } from 'primeng/accordion';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { PanelMenuModule } from 'primeng/panelmenu';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';

const routes: Routes = [
    {
        path: '',
        redirectTo: '/home',
        pathMatch: 'full',
    },
    {
        path: 'about',
        component: AboutComponent,
        canActivate: [authGuard],
    },
    {
        path: 'change-requests',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/change-requests-list/change-requests-list.module').then(
                (m) => m.ChangeRequestsListModule
            ),
    },
    {
        path: 'change-request-history',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/change-request-history/change-request-history.module').then(
                (m) => m.ChangeRequestHistoryModule
            ),
    },
    {
        path: 'communication-table',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/communication-table/communication-table.module').then(
                (m) => m.CommunicationTableModule
            ),
    },
    {
        path: 'crud',
        canActivate: [authGuard],
        loadChildren: () => import('./components/crud/crud.module').then((m) => m.CrudModule),
    },
    {
        path: 'dynamic-diagrams',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/diagrams/dynamic-diagram/dynamic-diagram.module').then(
                (m) => m.DynamicDiagramModule
            ),
    },
    {
        path: 'home',
        component: HomeComponent,
        canActivate: [authGuard],
    },
    {
        path: 'instancemodel',
        component: EasterEggComponent,
    },
    {
        path: 'login',
        component: LoginComponent,
        canActivate: [guestGuard],
    },
    {
        path: 'message-signal-allocation',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/message-signal-allocation/message-signal-allocation.module').then(
                (m) => m.MessageSignalAllocationModule
            ),
    },
    {
        path: 'summary-list',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/summary-list/summary-list.module').then((m) => m.routes),
    },
    {
        path: 'msc',
        canActivate: [authGuard],
        loadChildren: () => import('./components/msc/msc.module').then((m) => m.MSCModule),
    },
    {
        path: 'pending-changes',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/pending-changes/pending-changes.module').then(
                (m) => m.PendingChangesModule
            ),
    },

    {
        path: 'reports',
        canActivate: [authGuard],
        loadChildren: () => import('./components/report/report.module').then((m) => m.routes),
    },
    {
        path: 'static-diagrams',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/diagrams/static-diagram/static-diagram.module').then(
                (m) => m.DiagramModule
            ),
    },
    {
        path: 'structure-release-report',
        canActivate: [authGuard, writeAccessGuard],
        loadChildren: () =>
            import('./components/structure-release-report/structure-release-report.module').then(
                (m) => m.routes
            ),
    },
    {
        path: 'upload',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/msc-uploader/upload/upload.module').then((m) => m.UploadModule),
    },

    {
        path: 'maintenance-upload',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/msc-uploader/maintenance-upload/maintenance-upload.module').then(
                (m) => m.MaintenanceUploadModule
            ),
    },
    {
        path: 'current-db',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/current-db/current-db.module').then((m) => m.routes),
    },
    {
        path: 'lexicon-import',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/lexicon-import/lexicon-import.module').then(
                (m) => m.LexiconImportModule
            ),
    },
    {
        path: 'version-history',
        canActivate: [authGuard],
        loadChildren: () =>
            import('./components/version-history/version-history.module').then(
                (m) => m.VersionHistoryModule
            ),
    },
    {
        path: '404',
        component: PageNotFoundComponent,
    },
    {
        path: '**',
        redirectTo: '404',
    },
];

const metaReducers: MetaReducer[] = [storageMetaReducer];

@NgModule({
    declarations: [
        AppComponent,
        AboutComponent,
        BannerComponent,
        CreateFilterConfigDialogComponent,
        EasterEggComponent,
        LoginComponent,
        SidebarComponent,
        PageNotFoundComponent,
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        TreeComponent,
        BrowserModule,
        BrowserAnimationsModule,
        DropdownLazyComponent,
        DemoTagComponent,
        HomeComponent,
        FormsModule,
        ReactiveFormsModule,
        RouterModule.forRoot(routes),
        SearchLazyComponent,
        StoreModule.forRoot(
            {
                config: configReducer,
                userInfo: userInfoReducer,
            },
            {
                metaReducers,
            }
        ),
        StoreRouterConnectingModule.forRoot(),
        StoreDevtoolsModule.instrument({
            maxAge: 25,
            logOnly: environment.buildMode === BuildMode.PRODUCTION,
            connectInZone: true,
        }),
        WorkspaceDialogComponent,
        FavoriteToggleComponent,
        // PrimeNg
        AccordionModule,
        AvatarModule,
        BadgeModule,
        ButtonModule,
        CardModule,
        ChipModule,
        CheckboxModule,
        ConfirmDialogModule,
        ConfirmPopupModule,
        DialogModule,
        DividerModule,
        FieldsetModule,
        InputMaskModule,
        InputTextModule,
        PanelMenuModule,
        ProgressSpinnerModule,
        TableModule,
        ToastModule,
        TooltipModule,
        TagModule,
        CdkDragPlaceholder,
    ],
    providers: [
        provideAppInitializer(() => {
            const initializerFn = authServiceFactory(inject(AuthService));
            return initializerFn();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor,
            multi: true,
        },
        {
            provide: RouteReuseStrategy,
            useClass: RouteReuseService,
        },
        provideHttpClient(withInterceptorsFromDi()),
        ConfirmationService,
        MessageService,
    ],
})
export class AppModule {}
